import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import useReveal from "../lib/useReveal";
import Seo from "../components/Seo";
import { POSTS } from "../lib/blog";

function formatDate(iso) {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" });
}

export default function Blog() {
    useReveal();
    useEffect(() => window.scrollTo(0, 0), []);

    const [featured, ...rest] = POSTS;

    return (
        <main data-testid="blog-page" className="pt-40 md:pt-52">
            <Seo
                title="Blog — Insights from Cuttack's Premier B2B Caterer | The Food Gala"
                description="Behind-the-scenes stories, hospital meal planning, FSSAI hygiene standards, per-plate pricing guides and more — practical reading for anyone planning corporate or event catering in Odisha."
                path="/blog"
            />

            {/* HEADER */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-12 md:pb-16">
                <div className="grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-8 reveal">
                        <div className="text-[11px] uppercase tracking-[0.32em] text-[#C84B31] mb-6">
                            The Food Gala Journal
                        </div>
                        <h1 className="font-display text-5xl md:text-7xl leading-[0.98] tracking-tight">
                            Stories from the
                            <br />
                            <em className="not-italic text-[#1B4332]">kitchen and beyond.</em>
                        </h1>
                    </div>
                    <div className="md:col-span-4 reveal">
                        <p className="text-lg font-light text-[#1A1A1A]/75 leading-relaxed">
                            Hygiene playbooks, dietetics deep-dives, transparent pricing guides — written
                            for the people who decide what their teams and guests eat.
                        </p>
                    </div>
                </div>
            </section>

            {/* FEATURED */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16 md:pb-20" data-testid="blog-featured">
                <Link
                    to={`/blog/${featured.slug}`}
                    data-testid={`blog-featured-${featured.slug}`}
                    className="group grid md:grid-cols-12 gap-8 md:gap-12 items-center reveal"
                >
                    <div className="md:col-span-7 zoom-wrap aspect-[16/10]">
                        <img
                            src={featured.cover}
                            alt={featured.title}
                            loading="lazy"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="md:col-span-5">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="text-[10px] uppercase tracking-[0.32em] bg-[#1B4332] text-[#FAF9F6] px-3 py-1">
                                Featured
                            </span>
                            <span className="text-[11px] uppercase tracking-[0.28em] text-[#1A1A1A]/55">
                                {featured.category}
                            </span>
                        </div>
                        <h2 className="font-display text-3xl md:text-5xl leading-[1.05] tracking-tight group-hover:text-[#1B4332] transition-colors">
                            {featured.title}
                        </h2>
                        <p className="mt-5 text-lg font-light text-[#1A1A1A]/75 leading-relaxed">
                            {featured.excerpt}
                        </p>
                        <div className="mt-6 flex items-center gap-4 text-[11px] uppercase tracking-[0.28em] text-[#1A1A1A]/55">
                            <span>{formatDate(featured.date)}</span>
                            <span>·</span>
                            <span>{featured.readTime}</span>
                        </div>
                        <div className="mt-8 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.26em] text-[#1B4332] link-underline">
                            Read the full story <ArrowUpRight size={14} />
                        </div>
                    </div>
                </Link>
            </section>

            {/* GRID */}
            <section className="bg-[#F3F1EC] py-16 md:py-20">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="text-[11px] uppercase tracking-[0.32em] text-[#C84B31] mb-8 reveal">
                        More from the Journal
                    </div>
                    <div className="grid md:grid-cols-2 gap-8 md:gap-10">
                        {rest.map((p) => (
                            <Link
                                key={p.slug}
                                to={`/blog/${p.slug}`}
                                data-testid={`blog-card-${p.slug}`}
                                className="group bg-[#FAF9F6] border border-[#1B4332]/10 hover:border-[#1B4332] transition-all duration-500 reveal"
                            >
                                <div className="zoom-wrap aspect-[16/10] overflow-hidden">
                                    <img
                                        src={p.cover}
                                        alt={p.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="p-6 md:p-8">
                                    <div className="text-[10px] uppercase tracking-[0.32em] text-[#C84B31] mb-3">
                                        {p.category}
                                    </div>
                                    <h3 className="font-display text-2xl md:text-3xl leading-[1.1] tracking-tight group-hover:text-[#1B4332] transition-colors">
                                        {p.title}
                                    </h3>
                                    <p className="mt-3 text-[15px] font-light text-[#1A1A1A]/75 leading-relaxed">
                                        {p.excerpt}
                                    </p>
                                    <div className="mt-5 flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-[#1A1A1A]/55">
                                        <span>{formatDate(p.date)}</span>
                                        <span>·</span>
                                        <span>{p.readTime}</span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
}
